import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { TOPICS_DATA, TOPICS_ICONS } from '../constants/tagConstants';

function FavoriteTagsPanel() {
  const {
    user,
    favoriteTags,
    addFavoriteTag,
    removeFavoriteTag,
    eligibility
  } = useAuth();

  const [activeTopic, setActiveTopic] = useState(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);

  const isLoggedIn = !!user;

  const handleTopicClick = (topic) => {
    setActiveTopic(activeTopic === topic ? null : topic);
  };

  const handleToggle = async (path) => {
    if (!isLoggedIn || pending) return;
    setError('');
    const isSelected = favoriteTags.includes(path);
    if (!isSelected && !eligibility.favoriteTagAllowed) {
      setError(eligibility.favoriteTagNotAllowedReason || 'Cannot add more tags');
      return;
    }
    setPending(path);
    try {
      if (isSelected) {
        await removeFavoriteTag(path);
      } else {
        await addFavoriteTag(path);
      }
    } catch (err) {
      setError(err.message || 'Failed to update tag');
    } finally {
      setPending(null);
    }
  };

  const handleRemoveChip = async (tag) => {
    if (pending) return;
    setError('');
    setPending(tag);
    try {
      await removeFavoriteTag(tag);
    } catch (err) {
      setError(err.message || 'Failed to remove tag');
    } finally {
      setPending(null);
    }
  };

  const handleBack = () => {
    if (activeTopic) setActiveTopic(null);
  };

  const getSelectedCount = (topic) => {
    return favoriteTags.filter((tag) => {
      const [tagTopic] = tag.split('/');
      return tagTopic === topic;
    }).length;
  };

  const formatQuota = () => {
    const { favoriteTagUsage, favoriteTagQuota } = eligibility;
    if (favoriteTagQuota === 0) return 'Not included in your plan';
    if (!Number.isFinite(favoriteTagQuota)) {
      return `${favoriteTagUsage} followed · unlimited`;
    }
    return `${favoriteTagUsage} / ${favoriteTagQuota} followed`;
  };

  const SelectButton = ({ path, isSelected }) => {
    const disabled =
      !isLoggedIn ||
      pending === path ||
      (!isSelected && !eligibility.favoriteTagAllowed);
    return (
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          handleToggle(path);
        }}
        disabled={disabled}
        className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
          isSelected
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
        } disabled:opacity-40 disabled:cursor-not-allowed`}
        whileTap={disabled ? {} : { scale: 0.95 }}
      >
        {pending === path ? '...' : isSelected ? 'On' : 'Off'}
      </motion.button>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="bg-gray-50 rounded-xl p-12 text-center">
        <p className="text-gray-400 text-xs">Sign in to follow topics.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Quota row */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-gray-300 uppercase tracking-widest font-semibold">
          Followed topics
        </p>
        <p className="text-[10px] text-gray-400 tabular-nums">{formatQuota()}</p>
      </div>

      {/* Selected tag chips */}
      {favoriteTags.length > 0 && (
        <motion.div
          className="flex flex-wrap gap-1.5 p-3 bg-purple-50 rounded-lg border border-purple-100"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {favoriteTags.map((tag) => {
            const parts = tag.split('/');
            const leaf = parts[parts.length - 1];
            const trail = parts.slice(0, -1).join('/');
            return (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold bg-white text-purple-700 border border-purple-100"
              >
                {leaf}
                {trail && (
                  <>
                    <span className="mx-1 text-gray-300">in</span>
                    <span className="text-gray-400 font-normal">{trail}</span>
                  </>
                )}
                <button
                  onClick={() => handleRemoveChip(tag)}
                  disabled={pending === tag}
                  className="ml-1.5 text-gray-300 hover:text-red-500 transition-colors font-bold disabled:opacity-40"
                  aria-label={`Unfollow ${leaf}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </motion.div>
      )}

      {/* Error message */}
      {error && (
        <div className="text-[11px] text-red-500 bg-red-50 rounded px-3 py-2 border border-red-100">
          {error}
        </div>
      )}

      {/* Breadcrumb */}
      {activeTopic && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 text-xs"
        >
          <button
            onClick={handleBack}
            className="text-purple-500 hover:text-purple-700 transition-colors font-semibold"
          >
            &lt;- Back
          </button>
          <span className="text-gray-200">|</span>
          <span className="text-gray-500 text-[11px]">{activeTopic}</span>
        </motion.div>
      )}

      {/* Content drill-down: topic → subtopic (two levels only) */}
      <AnimatePresence mode="wait">
        {!activeTopic ? (
          <motion.div
            key="topics"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          >
            {Object.entries(TOPICS_DATA.topics).map(([topic]) => {
              const IconComponent = TOPICS_ICONS[topic];
              const selectedCount = getSelectedCount(topic);
              const isSelected = favoriteTags.includes(topic);
              return (
                <motion.div
                  key={topic}
                  className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                    selectedCount > 0
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-100 bg-white hover:border-purple-200 hover:shadow-card'
                  }`}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleTopicClick(topic)}
                      className="flex items-center text-xs font-semibold text-gray-800 flex-grow text-left"
                    >
                      {IconComponent && (
                        <IconComponent className="w-3.5 h-3.5 mr-2 text-purple-400" />
                      )}
                      <span>{topic}</span>
                      {selectedCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-purple-100 text-purple-700 rounded font-bold tabular-nums">
                          {selectedCount}
                        </span>
                      )}
                    </button>
                    <div className="flex items-center">
                      <SelectButton path={topic} isSelected={isSelected} />
                      <span className="ml-1.5 text-gray-200 text-xs">→</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="subtopics"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-1.5"
          >
            {Object.entries(TOPICS_DATA.topics[activeTopic]).map(([subtopic]) => {
              const fullPath = `${activeTopic}/${subtopic}`;
              const isSelected = favoriteTags.includes(fullPath);
              return (
                <motion.div
                  key={subtopic}
                  className={`p-3 rounded-lg border transition-all ${
                    isSelected
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-100 bg-white hover:border-purple-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">{subtopic}</span>
                    <SelectButton path={fullPath} isSelected={isSelected} />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FavoriteTagsPanel;
